import React, { useState, useEffect, useCallback } from "react";
import { flushSync } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Tab,
  Tabs,
  Card,
  CardContent,
  CardActionArea,
  Fab,
  Chip,
  Avatar,
  Stack,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Collapse,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import GroupsIcon from "@mui/icons-material/Groups";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HistoryIcon from "@mui/icons-material/History";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckIcon from "@mui/icons-material/Check";
import HandshakeIcon from "@mui/icons-material/Handshake";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAuth } from "../contexts/AuthContext";
import { useOffline } from "../contexts/OfflineContext";
import { apiGet, apiPut, apiPost, apiDelete } from "../utils/apiClient";
import FamilyManager from "../components/FamilyManager";
import PullToRefresh from "../components/PullToRefresh";
import useAutoRefresh from "../hooks/useAutoRefresh";
import { HEADER_GRADIENT, HEADER_TEXT } from "../App";

const CATEGORY_STYLE = {
  food:          { emoji: "🍽️", color: "#95E1D3" },
  transport:     { emoji: "🚗", color: "#EAFFD0" },
  accommodation: { emoji: "🏨", color: "#FFD3A5" },
  activities:    { emoji: "🎯", color: "#C9B3FF" },
  other:         { emoji: "🧳", color: "#E0EAF4" },
};

const FAMILY_PALETTE = ["#A8D8EA", "#FCE38A", "#95E1D3", "#FFAAA5", "#C9B3FF", "#EAFFD0"];
const familyColor = (id) => FAMILY_PALETTE[(id || 0) % FAMILY_PALETTE.length];

function formatAction(action, details) {
  const d = details || {};
  switch (action) {
    case "expense_added":         return `added expense "${d.description}" (${d.amount})`;
    case "expense_updated":       return `updated expense "${d.description}" (${d.amount})`;
    case "expense_deleted":       return `deleted expense "${d.description}"`;
    case "family_created":        return `created family "${d.name}"`;
    case "family_renamed":        return `renamed family "${d.old_name}" → "${d.name}"`;
    case "family_deleted":        return `deleted family "${d.name}"`;
    case "family_member_added":   return `added ${d.user_name} to family "${d.family_name}"`;
    case "family_member_removed": return `removed ${d.user_name} from family "${d.family_name}"`;
    case "member_approved":       return `approved ${d.user_name} to join the trip`;
    case "member_rejected":       return `rejected ${d.user_name}'s request`;
    case "settlement_recorded":   return `recorded settlement: ${d.from_family_name} → ${d.to_family_name} (${d.amount})`;
    case "settlement_deleted":    return `deleted settlement: ${d.from_family_name} → ${d.to_family_name}`;
    case "trip_created":          return `created the trip "${d.name}"`;
    case "trip_updated":          return `updated trip details`;
    case "trip_deleted":          return `deleted the trip`;
    default:                      return action.replace(/_/g, " ");
  }
}

export default function TripDetail() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshCount } = useOffline();
  const [tab, setTab] = useState(0);
  const [trip, setTrip] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [families, setFamilies] = useState([]);
  const [balances, setBalances] = useState({ transfers: [], summary: [] });
  const [settlements, setSettlements] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [activity, setActivity] = useState([]);
  const [settleOpen, setSettleOpen] = useState(false);
  const [categoryExpanded, setCategoryExpanded] = useState(false);
  const [settlementExpanded, setSettlementExpanded] = useState(false);
  const [settleForm, setSettleForm] = useState({ from_family_id: "", to_family_id: "", amount: "", note: "" });
  const [editSettlement, setEditSettlement] = useState(null); // settlement being edited
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [settingsSection, setSettingsSection] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [editTripOpen, setEditTripOpen] = useState(false);
  const [editTripForm, setEditTripForm] = useState({ name: "", description: "", currency: "" });
  const [deleteTripConfirm, setDeleteTripConfirm] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [snackError, setSnackError] = useState("");

  const isOwner = trip?.created_by === user?.id;

  const load = useCallback(async () => {
    try {
      const tripData = await apiGet(`/api/trips/${tripId}`);
      setTrip(tripData);
    } catch (err) {
      console.error("Failed to load trip:", err);
      const status = err?.response?.status;
      const msg = err?.response?.data?.error;
      if (status === 403 && msg === "Pending approval") {
        setLoadError("pending");
      } else if (status === 404 || status === 403) {
        setLoadError("Trip not found.");
      } else {
        setLoadError("Could not load trip. Please try again.");
      }
      return;
    }
    let hadError = false;
    const safe = async (fn, fallback) => { try { return await fn(); } catch (e) { console.error(e); hadError = true; return fallback; } };
    const [expData, famData, balData, setData, actData] = await Promise.all([
      safe(() => apiGet(`/api/trips/${tripId}/expenses`),             []),
      safe(() => apiGet(`/api/trips/${tripId}/families`),             []),
      safe(() => apiGet(`/api/trips/${tripId}/settlements/balances`), { transfers: [], summary: [] }),
      safe(() => apiGet(`/api/trips/${tripId}/settlements`),          []),
      safe(() => apiGet(`/api/trips/${tripId}/activity`),             []),
    ]);
    setExpenses([...expData].sort((a, b) =>
      new Date(b.date || b.created_at) - new Date(a.date || a.created_at) || b.id - a.id
    ));
    setFamilies(famData);
    setBalances(balData);
    setSettlements(setData);
    setActivity(actData);
    if (hadError) setSnackError("Some data failed to load. Please try again.");
  }, [tripId]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, 30000);

  useEffect(() => {
    if (isOwner) {
      apiGet(`/api/trips/${tripId}/applicants`).then(setApplicants).catch(() => {});
    }
  }, [tripId, isOwner]);

  const handleEditTripSave = async () => {
    const updated = await apiPut(`/api/trips/${tripId}`, editTripForm);
    setTrip(updated);
    setEditTripOpen(false);
  };

  const handleApprove = async (userId, status) => {
    await apiPut(`/api/trips/${tripId}/members/${userId}`, { status });
    await refreshCount();
    setApplicants((prev) => prev.filter((a) => a.id !== userId));
    load();
  };

  const handleDeleteExpense = async (expId) => {
    await apiDelete(`/api/trips/${tripId}/expenses/${expId}`);
    await refreshCount();
    setExpenses((prev) => prev.filter((e) => e.id !== expId));
    const balData = await apiGet(`/api/trips/${tripId}/settlements/balances`);
    setBalances(balData);
  };

  const handleSettle = async () => {
    await apiPost(`/api/trips/${tripId}/settlements`, settleForm);
    await refreshCount();
    setSettleOpen(false);
    setSettleForm({ from_family_id: "", to_family_id: "", amount: "", note: "" });
    const [balData, setData, actData] = await Promise.all([
      apiGet(`/api/trips/${tripId}/settlements/balances`),
      apiGet(`/api/trips/${tripId}/settlements`),
      apiGet(`/api/trips/${tripId}/activity`),
    ]);
    setBalances(balData);
    setSettlements(setData);
    setActivity(actData);
  };

  const handleEditSettlementSave = async () => {
    // Backend has no PUT — delete old and recreate with updated values
    await apiDelete(`/api/trips/${tripId}/settlements/${editSettlement.id}`);
    await apiPost(`/api/trips/${tripId}/settlements`, {
      from_family_id: editSettlement.from_family_id,
      to_family_id: editSettlement.to_family_id,
      amount: editSettlement.amount,
      note: editSettlement.note,
    });
    await refreshCount();
    setEditSettlement(null);
    const [balData, setData, actData] = await Promise.all([
      apiGet(`/api/trips/${tripId}/settlements/balances`),
      apiGet(`/api/trips/${tripId}/settlements`),
      apiGet(`/api/trips/${tripId}/activity`),
    ]);
    setBalances(balData);
    setSettlements(setData);
    setActivity(actData);
  };

  const handleDeleteSettlement = async (settlementId) => {
    await apiDelete(`/api/trips/${tripId}/settlements/${settlementId}`);
    await refreshCount();
    setEditSettlement(null);
    const [balData, setData, actData] = await Promise.all([
      apiGet(`/api/trips/${tripId}/settlements/balances`),
      apiGet(`/api/trips/${tripId}/settlements`),
      apiGet(`/api/trips/${tripId}/activity`),
    ]);
    setBalances(balData);
    setSettlements(setData);
    setActivity(actData);
  };

  if (loadError) {
    const isPending = loadError === "pending";
    const isNotFound = loadError === "Trip not found.";
    const emoji = isPending ? "⏳" : isNotFound ? "🔍" : "⚠️";
    const message = isPending ? "Your request to join this trip is awaiting approval." : loadError;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: 2, p: 3 }}>
        <Typography fontSize="3rem">{emoji}</Typography>
        <Typography fontWeight={700} variant="h6" textAlign="center">{message}</Typography>
        {!isNotFound && !isPending && <Button variant="contained" onClick={() => { setLoadError(null); load(); }}>Retry</Button>}
        <Button onClick={() => navigate("/")} variant={isNotFound || isPending ? "contained" : "text"}>Back to My Trips</Button>
      </Box>
    );
  }
  if (!trip) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
      <Typography color="text.secondary">Loading…</Typography>
    </Box>
  );

  const currency = trip.currency || "EUR";

  return (
    <Box sx={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Gradient header */}
      <Box sx={{ background: HEADER_GRADIENT, px: 1, pt: 1, pb: 0, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <IconButton size="small" onClick={() => navigate("/")} sx={{ color: HEADER_TEXT }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={700} noWrap sx={{ flex: 1, color: HEADER_TEXT, textAlign: "center" }}>
            {trip.name}
          </Typography>
          {applicants.length > 0 && (
            <Chip label={`${applicants.length} pending`} size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.5)", color: HEADER_TEXT, fontWeight: 700 }} />
          )}
          <IconButton size="small" onClick={(e) => setSettingsAnchorEl(e.currentTarget)} sx={{ color: HEADER_TEXT }}>
            <SettingsIcon />
          </IconButton>
          <Menu anchorEl={settingsAnchorEl} open={Boolean(settingsAnchorEl)} onClose={() => setSettingsAnchorEl(null)}>
            {isOwner && (
              <MenuItem onClick={() => { setSettingsAnchorEl(null); setEditTripForm({ name: trip.name, description: trip.description || "", currency: trip.currency }); setEditTripOpen(true); }}>
                <SettingsIcon fontSize="small" sx={{ mr: 1 }} /> Edit Trip
              </MenuItem>
            )}
            <MenuItem onClick={() => { setSettingsAnchorEl(null); setShareOpen(true); }}>
              <ShareIcon fontSize="small" sx={{ mr: 1 }} /> Share Trip
            </MenuItem>
            <MenuItem onClick={() => { setSettingsAnchorEl(null); setSettingsSection("families"); }}>
              <GroupsIcon fontSize="small" sx={{ mr: 1 }} /> Families
            </MenuItem>
            <MenuItem onClick={() => { setSettingsAnchorEl(null); setSettingsSection("activity"); }}>
              <HistoryIcon fontSize="small" sx={{ mr: 1 }} /> Activity
            </MenuItem>
          </Menu>
        </Box>

        {/* Pill tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            "& .MuiTabs-flexContainer": { gap: 1 },
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              borderRadius: "20px",
              minHeight: 36,
              px: 2,
              py: 0.5,
              color: "rgba(45,52,54,0.45)",
              fontWeight: 700,
              fontSize: "0.75rem",
              letterSpacing: 0.3,
              textTransform: "uppercase",
              fontFamily: "'Quicksand', sans-serif",
              minWidth: 0,
            },
            "& .Mui-selected": {
              bgcolor: "rgba(255,255,255,0.92)",
              color: "#1a1a1a",
              fontWeight: 800,
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            },
          }}
        >
          <Tab icon={<ReceiptIcon fontSize="small" />} iconPosition="start" label="Expenses" />
          <Tab icon={<AccountBalanceIcon fontSize="small" />} iconPosition="start" label="Balances" />
        </Tabs>
      </Box>

      {/* Scrollable content */}
      <PullToRefresh onRefresh={load} sx={{ flex: 1, overflowY: "auto", overscrollBehavior: "contain" }}>
        <Container maxWidth="sm" sx={{ py: 2.5, pb: 12 }}>

          {/* Join Requests */}
          {isOwner && applicants.length > 0 && (
            <Paper elevation={0} sx={{ p: 2.5, mb: 2 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                Join Requests
              </Typography>
              <List dense disablePadding sx={{ mt: 1 }}>
                {applicants.map((a) => (
                  <ListItem key={a.id} disablePadding sx={{ py: 0.5 }}>
                    <ListItemAvatar>
                      <Avatar src={a.avatar_url} sx={{ width: 32, height: 32 }}>{a.name[0]}</Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={a.name} secondary={a.email} />
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={() => handleApprove(a.id, "approved")}
                        sx={{ color: "#95E1D3" }}><CheckIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleApprove(a.id, "rejected")}
                        sx={{ color: "#FFAAA5" }}><CloseIcon fontSize="small" /></IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {/* ── Expenses tab ── */}
          {tab === 0 && (
            expenses.length === 0
              ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 6, gap: 2 }}>
                  <Typography color="text.secondary">No expenses yet. Add one!</Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setShareOpen(true)}
                    sx={{ borderRadius: 50, textTransform: "none", fontWeight: 700 }}
                  >
                    ✉️ Invite Travelers
                  </Button>
                </Box>
              )
              : expenses.map((exp) => {
                  const cat = CATEGORY_STYLE[exp.category] || CATEGORY_STYLE.other;
                  const validSplits = (exp.splits || []).filter(s => s.family_name && parseFloat(s.amount) > 0);
                  return (
                    <Card key={exp.id} sx={{ mb: 1.5 }}>
                      <CardActionArea
                        onClick={() => navigate(`/trips/${tripId}/expenses/${exp.id}/edit`)}
                        sx={{ borderRadius: 24 }}
                      >
                        <CardContent sx={{ p: 3, pb: "20px !important" }}>
                          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                            {/* Category icon bubble */}
                            <Box sx={{
                              width: 44, height: 44, borderRadius: "50%",
                              bgcolor: cat.color, flexShrink: 0,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 22,
                            }}>
                              {cat.emoji}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <Typography fontWeight={700} fontSize="1rem" noWrap sx={{ flex: 1, mr: 1 }}>
                                  {exp.description}
                                </Typography>
                                <Typography fontWeight={700} fontSize="1rem" color="text.primary" sx={{ whiteSpace: "nowrap" }}>
                                  {currency} {parseFloat(exp.amount).toFixed(2)}
                                </Typography>
                              </Box>

                              <Typography variant="caption" color="text.secondary">
                                Paid by {exp.paid_by_name} · {new Date(exp.date).toLocaleDateString()}
                              </Typography>

                              {/* Family split row */}
                              {validSplits.length > 0 ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                                  {validSplits.map((s) => (
                                    <Box key={s.family_id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <Box sx={{
                                        width: 20, height: 20, borderRadius: "50%",
                                        bgcolor: familyColor(s.family_id),
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        flexShrink: 0,
                                      }}>
                                        <Typography fontSize={9} fontWeight={700} color={HEADER_TEXT}>
                                          {s.family_name?.[0]}
                                        </Typography>
                                      </Box>
                                      <Typography variant="caption" color="text.secondary">
                                        {s.family_name}: {currency} {parseFloat(s.amount).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
                                  No families set up
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  );
                })
          )}

          {/* ── Balances tab ── */}
          {tab === 1 && (
            <>
              {/* Per-family summary */}
              {balances.summary.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 2, pb: "12px !important" }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 1 }}>
                      Summary
                    </Typography>
                    {/* Header row */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Typography variant="caption" color="text.disabled" sx={{ flex: 1, mr: 1 }}>Family</Typography>
                      <Box sx={{ display: "flex", gap: 2, flexShrink: 0 }}>
                        <Typography variant="caption" color="text.disabled" sx={{ minWidth: 52, textAlign: "right" }}>Paid</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ minWidth: 52, textAlign: "right" }}>Share</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ minWidth: 52, textAlign: "right" }}>Net</Typography>
                      </Box>
                    </Box>
                    <Stack spacing={0.75}>
                      {balances.summary.map((s) => (
                        <Box key={s.family_id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, mr: 1 }}>
                            {s.family_name}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 2, flexShrink: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 52, textAlign: "right" }}>
                              <strong>{s.total_paid.toFixed(2)}</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 52, textAlign: "right" }}>
                              <strong>{s.total_share.toFixed(2)}</strong>
                            </Typography>
                            <Typography variant="caption" fontWeight={700}
                              sx={{ color: s.net > 0.005 ? "#00B894" : s.net < -0.005 ? "#FFAAA5" : "text.secondary", minWidth: 52, textAlign: "right" }}>
                              {s.net > 0.005 ? "+" : ""}{s.net.toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                    {/* Category breakdown toggle */}
                    {expenses.length > 0 && (
                      <>
                        <Divider sx={{ mt: 1.25, mb: 0.5 }} />
                        <Box
                          onClick={() => setCategoryExpanded((v) => !v)}
                          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", pt: 0.5 }}
                        >
                          <Typography variant="caption" fontWeight={700} color="text.secondary"
                            sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                            Spending by category
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Typography variant="caption" fontWeight={700}>
                              {currency} {expenses.reduce((s, e) => s + parseFloat(e.amount), 0).toFixed(2)}
                            </Typography>
                            <ExpandMoreIcon
                              fontSize="small"
                              sx={{
                                color: "text.secondary",
                                transform: categoryExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s",
                              }}
                            />
                          </Box>
                        </Box>
                        <Collapse in={categoryExpanded}>
                          <Stack spacing={0.5} sx={{ mt: 1 }}>
                            {Object.entries(
                              expenses.reduce((acc, e) => {
                                const key = e.category || "other";
                                acc[key] = (acc[key] || 0) + parseFloat(e.amount);
                                return acc;
                              }, {})
                            )
                              .sort((a, b) => b[1] - a[1])
                              .map(([cat, total]) => {
                                const style = CATEGORY_STYLE[cat] || CATEGORY_STYLE.other;
                                return (
                                  <Box key={cat} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                      <Typography fontSize="0.95rem" lineHeight={1}>{style.emoji}</Typography>
                                      <Typography variant="caption" sx={{ textTransform: "capitalize" }}>{cat}</Typography>
                                    </Box>
                                    <Typography variant="caption" fontWeight={700}>
                                      {currency} {total.toFixed(2)}
                                    </Typography>
                                  </Box>
                                );
                              })}
                          </Stack>
                        </Collapse>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Transfers */}
              {balances.transfers.filter((b) => b.amount > 0).length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: "center", mt: 6 }}>All settled up! 🎉</Typography>
              ) : (
                balances.transfers.filter((b) => b.amount > 0).map((b, i) => (
                  <Card key={i} sx={{ mb: 1.5 }}>
                    <CardContent sx={{ p: 3, pb: "20px !important" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography fontWeight={700}>{b.from_family_name} → {b.to_family_name}</Typography>
                          <Typography variant="caption" color="text.secondary">owes</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography fontWeight={700} fontSize="1.05rem" sx={{ color: "#FFAAA5" }}>
                            {currency} {b.amount.toFixed(2)}
                          </Typography>
                          <IconButton
                            size="small"
                            sx={{ color: "#00B894" }}
                            onClick={() => {
                              setSettleForm({ from_family_id: String(b.from_family_id), to_family_id: String(b.to_family_id), amount: b.amount.toFixed(2), note: "" });
                              setSettleOpen(true);
                            }}
                          >
                            <HandshakeIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}

              {settlements.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    onClick={() => setSettlementExpanded((v) => !v)}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                  >
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
                      Settlement History ({settlements.length})
                    </Typography>
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        color: "text.secondary",
                        transform: settlementExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s",
                      }}
                    />
                  </Box>
                  <Collapse in={settlementExpanded}>
                    <Stack divider={<Divider />} sx={{ mt: 1 }}>
                      {settlements.map((s) => (
                        <Box key={s.id} sx={{ py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2">{s.from_family_name} → {s.to_family_name}</Typography>
                            {s.note && <Typography variant="caption" color="text.secondary">{s.note}</Typography>}
                          </Box>
                          <Typography variant="body2" fontWeight={700} sx={{ color: "#95E1D3", whiteSpace: "nowrap" }}>
                            {currency} {parseFloat(s.amount).toFixed(2)}
                          </Typography>
                          <IconButton size="small" sx={{ color: "text.secondary", flexShrink: 0 }}
                            onClick={(e) => { e.stopPropagation(); setEditSettlement({
                              id: s.id,
                              from_family_id: s.from_family_id,
                              to_family_id: s.to_family_id,
                              amount: s.amount,
                              note: s.note || "",
                            }); }}>
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              )}

              <Button variant="contained" fullWidth sx={{ mt: 2.5 }} onClick={() => setSettleOpen(true)}>
                Record Settlement
              </Button>
            </>
          )}
        </Container>
      </PullToRefresh>

      {tab === 0 && (
        <Fab color="primary" sx={{ position: "fixed", bottom: 32, right: 24 }}
          onClick={() => navigate(`/trips/${tripId}/expenses/new`)}>
          <AddIcon sx={{ color: HEADER_TEXT }} />
        </Fab>
      )}

      {/* Settings full-screen dialog */}
      <Dialog open={Boolean(settingsSection)} onClose={() => setSettingsSection(null)} fullScreen
        PaperProps={{ sx: { borderRadius: 0, background: "linear-gradient(160deg, #EBF3F8 0%, #F8F3E8 100%)", display: "flex", flexDirection: "column", overflow: "hidden" } }}>
        <Box sx={{ background: HEADER_GRADIENT, px: 1, pt: 1, pb: 1, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton size="small" onClick={() => setSettingsSection(null)} sx={{ color: HEADER_TEXT }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={700} color={HEADER_TEXT}>
              {settingsSection === "families" ? "Families" : "Activity"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Container maxWidth="sm" sx={{ py: 2.5 }}>
          {settingsSection === "families" && (
            <FamilyManager tripId={tripId} families={families} members={trip.members || []}
              isOwner={isOwner} createdBy={trip.created_by} onFamiliesChange={setFamilies} />
          )}
          {settingsSection === "activity" && (
            activity.length === 0
              ? <Typography color="text.secondary" sx={{ textAlign: "center", mt: 6 }}>No activity yet.</Typography>
              : <Stack divider={<Divider />}>
                  {activity.map((a) => (
                    <Box key={a.id} sx={{ py: 1.5, display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Avatar src={a.user_avatar} sx={{ width: 30, height: 30, mt: 0.3, flexShrink: 0 }}>
                        {a.user_name?.[0]}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2">
                          <strong>{a.user_name ?? "Someone"}</strong>{" "}{formatAction(a.action, a.details)}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {new Date(a.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
          )}
        </Container>
        </Box>
      </Dialog>

      {/* Settle dialog */}
      <Dialog open={settleOpen} onClose={() => setSettleOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Record Settlement 💸</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField select label="From family" fullWidth value={settleForm.from_family_id}
              onChange={(e) => setSettleForm({ ...settleForm, from_family_id: e.target.value })}
              SelectProps={{ native: true }}>
              <option value="" />
              {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </TextField>
            <TextField select label="To family" fullWidth value={settleForm.to_family_id}
              onChange={(e) => setSettleForm({ ...settleForm, to_family_id: e.target.value })}
              SelectProps={{ native: true }}>
              <option value="" />
              {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </TextField>
            <TextField label={`Amount (${currency})`} type="number" fullWidth value={settleForm.amount}
              onChange={(e) => setSettleForm({ ...settleForm, amount: e.target.value })}
              inputProps={{ min: 0, step: "0.01" }} />
            <TextField label="Note (optional)" fullWidth value={settleForm.note}
              onChange={(e) => setSettleForm({ ...settleForm, note: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setSettleOpen(false)} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" onClick={handleSettle}
            disabled={!settleForm.from_family_id || !settleForm.to_family_id || !settleForm.amount}>
            Record
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Trip dialog */}
      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Share Trip 🔑</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Share this code or link so others can request to join.
          </Typography>
          {/* Big code display */}
          <Box sx={{
            background: HEADER_GRADIENT, borderRadius: 4, py: 3, textAlign: "center", mb: 2,
          }}>
            <Typography fontWeight={800} fontSize="2.5rem" letterSpacing="0.4em" color={HEADER_TEXT}
              sx={{ fontFamily: "monospace" }}>
              {trip?.share_code}
            </Typography>
          </Box>
          {/* Copyable link */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1,
            bgcolor: "rgba(168,216,234,0.15)", borderRadius: 3, px: 2, py: 1.5,
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, wordBreak: "break-all" }}>
              {window.location.origin}/{trip?.share_code}
            </Typography>
            <IconButton size="small" onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/${trip?.share_code}`);
            }}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setShareOpen(false)} sx={{ color: "text.secondary" }}>Close</Button>
          {navigator.share && (
            <Button variant="contained" startIcon={<ShareIcon />} onClick={() => {
              navigator.share({
                title: trip?.name,
                text: `Join my trip "${trip?.name}" on TravelSplit!`,
                url: `${window.location.origin}/${trip?.share_code}`,
              });
            }}>
              Share
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Trip dialog */}
      <Dialog open={editTripOpen} onClose={() => setEditTripOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>Edit Trip ✈️</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1.5 }}>
            <TextField label="Trip name" fullWidth value={editTripForm.name}
              onChange={(e) => setEditTripForm({ ...editTripForm, name: e.target.value })} />
            <TextField label="Description (optional)" fullWidth multiline rows={2}
              value={editTripForm.description}
              onChange={(e) => setEditTripForm({ ...editTripForm, description: e.target.value })} />
            <TextField label="Currency" fullWidth value={editTripForm.currency}
              onChange={(e) => setEditTripForm({ ...editTripForm, currency: e.target.value })}
              inputProps={{ maxLength: 10 }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTripConfirm(true)} sx={{ color: "#FFAAA5", mr: "auto" }}>
            Delete
          </Button>
          <Button onClick={() => setEditTripOpen(false)} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" onClick={handleEditTripSave} disabled={!editTripForm.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Trip confirm dialog */}
      <Dialog open={deleteTripConfirm} onClose={() => setDeleteTripConfirm(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Trip?</DialogTitle>
        <DialogContent>
          <Typography>This will delete <strong>{trip?.name}</strong> and all its data. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTripConfirm(false)} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={async () => {
            await apiDelete(`/api/trips/${tripId}`);
            flushSync(() => setDeleteTripConfirm(false));
            document.getElementById("root")?.removeAttribute("aria-hidden");
            document.body.style.overflow = "";
            navigate("/");
          }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Settlement dialog */}
      <Dialog open={Boolean(editSettlement)} onClose={() => setEditSettlement(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Settlement ✏️</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField select label="From family" fullWidth value={editSettlement?.from_family_id ?? ""}
              onChange={(e) => setEditSettlement({ ...editSettlement, from_family_id: e.target.value })}
              SelectProps={{ native: true }}>
              <option value="" />
              {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </TextField>
            <TextField select label="To family" fullWidth value={editSettlement?.to_family_id ?? ""}
              onChange={(e) => setEditSettlement({ ...editSettlement, to_family_id: e.target.value })}
              SelectProps={{ native: true }}>
              <option value="" />
              {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </TextField>
            <TextField label={`Amount (${currency})`} type="number" fullWidth
              value={editSettlement?.amount ?? ""}
              onChange={(e) => setEditSettlement({ ...editSettlement, amount: e.target.value })}
              inputProps={{ min: 0, step: "0.01" }} />
            <TextField label="Note (optional)" fullWidth value={editSettlement?.note ?? ""}
              onChange={(e) => setEditSettlement({ ...editSettlement, note: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => handleDeleteSettlement(editSettlement?.id)}
            sx={{ color: "#FFAAA5", mr: "auto" }}>
            Delete
          </Button>
          <Button onClick={() => setEditSettlement(null)} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSettlementSave}
            disabled={!editSettlement?.from_family_id || !editSettlement?.to_family_id || !editSettlement?.amount}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snackError)} autoHideDuration={4000} onClose={() => setSnackError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="error" onClose={() => setSnackError("")} sx={{ width: "100%" }}>
          {snackError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
